import type { AdminOrganizationDetail } from '../../../domain/superadmin/types';
import { formatCurrency } from '../../../utils/formatters';
import { cn } from '@/lib/utils';

type OrgKpiRowProps = {
  org: AdminOrganizationDetail['org'];
  usage: AdminOrganizationDetail['usage'];
};

const formatPlanLabel = (plan: string | null) => {
  if (!plan) return '—';
  return plan.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatLimit = (max: number) => (max === -1 ? '∞' : String(max));

function KpiCard({
  label,
  value,
  subtitle,
  valueClassName,
}: {
  label: string;
  value: string;
  subtitle?: string;
  valueClassName?: string;
}) {
  return (
    <li className="relative overflow-hidden rounded-xl border border-border bg-surface px-s4 py-3.5">
      <span
        className="absolute inset-x-0 top-0 h-0.5 rounded-t-xl bg-primary"
        aria-hidden
      />
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-muted">
        {label}
      </p>
      <p
        className={cn(
          'font-mono text-[22px] font-semibold leading-none tracking-[-0.5px] tabular-nums text-default md:text-[26px]',
          valueClassName,
        )}
      >
        {value}
      </p>
      {subtitle && <p className="mt-1 text-[11.5px] text-muted">{subtitle}</p>}
    </li>
  );
}

export function OrganizationKpiRow({ org, usage }: OrgKpiRowProps) {
  return (
    <section aria-label="Indicadores da empresa" className="mb-s5">
      <ul className="grid grid-cols-2 gap-s3 lg:grid-cols-4">
        <KpiCard label="Plano" value={formatPlanLabel(org.plan)} subtitle="Assinatura atual" />
        <KpiCard
          label="Valor (MRR)"
          value={formatCurrency(org.mrrCents / 100)}
          subtitle="Receita recorrente mensal"
        />
        <KpiCard
          label="Usuários"
          value={`${usage.users.current}/${formatLimit(usage.users.max)}`}
          subtitle="Uso do limite do plano"
        />
        <KpiCard
          label="WhatsApp"
          value={`${usage.whatsapp.current}/${formatLimit(usage.whatsapp.max)}`}
          subtitle="Limite do plano"
        />
      </ul>
    </section>
  );
}
